import { createContext, useContext } from "react";
import type { ApiClient } from "../api/client";
import type {
  Authorization,
  Call,
  DeviceStatus,
  DeviceSummary,
  DeliverySettings,
  FloorRoom,
  GeneralSettings,
  MenuResponse,
  Order,
  PaymentMethod,
  PrintStatus,
  CodePurpose,
  SoldOutProduct,
  Promotions,
  Street,
  TimeStatus,
} from "../api/types";

/** What every screen of the till needs. */
export interface Till {
  api: ApiClient;
  status: DeviceStatus;
  menu: MenuResponse;
  /** Rooms with tables and decoration, for "Kies tafel". */
  floor: FloorRoom[];
  /** The plan after a change made here in Beheer › Tafelplan (the live update follows elsewhere). */
  floorChanged: (rooms: FloorRoom[]) => void;
  /** The open order per occupied table (label as on the plan), live from every device. */
  openTables: ReadonlyMap<string, Order>;
  /** Puts an order that was just saved in the list at once (the live update follows). */
  orderSaved: (order: Order) => void;
  /** The menu after a change made here in Beheer (the live update follows for the other devices). */
  menuChanged: (menu: MenuResponse) => void;
  /** Loads the menu again, e.g. after it was changed elsewhere meanwhile. */
  reloadMenu: () => void;
  /** Products sold out now (ids), live from every device: their tiles cannot be tapped. */
  soldOut: ReadonlySet<string>;
  /** The list after a change made here (the live update follows for the other devices). */
  soldOutChanged: (products: SoldOutProduct[]) => void;
  /** The shop's actions (Acties), live from every device; null until loaded. */
  promotions: Promotions | null;
  /** The actions after a change made here in Beheer (the live update follows for the other devices). */
  promotionsChanged: (promotions: Promotions) => void;
  /** Goes up when the staff changed on any device (Beheer › Personeel & rechten reloads). */
  staffRevision: number;
  /** Delivery zones, standard fee and hours (Leveringszones), live from every device; null until loaded. */
  delivery: DeliverySettings | null;
  /** The zones after a change made here in Beheer (the live update follows elsewhere). */
  deliveryChanged: (delivery: DeliverySettings) => void;
  /** The shop's streets per (deel)gemeente with their distance, live: suggestions for a delivery. */
  streets: readonly Street[];
  /** Goes up when a customer changed on any device (the card on the ticket and Beheer › Klanten reload). */
  customersRevision: number;
  /** The call ringing now (Nummerherkenning), until it is taken or ignored here or taken elsewhere. */
  incomingCall: Call | null;
  /** The call was taken over or ignored on this till. */
  callHandled: () => void;
  /** Goes up with every call and every call taken (the Bellerslijst reloads). */
  callsRevision: number;
  /** Algemeen: the shop's details, hours and receipt text, live from every device; null until loaded. */
  general: GeneralSettings | null;
  /** After a change made here in Beheer › Algemeen (the live update follows elsewhere). */
  generalChanged: (general: GeneralSettings) => void;
  /** The shop prints a Bancontact Pro QR on the receipt: the checkout offers it (ADR 0029). */
  receiptQr: boolean;
  /** Goes up when printers, receipt rules, kitchen routing or devices changed on any device. */
  printingRevision: number;
  /**
   * Afdrukken (blok I): the shop's printers, the computers running the Tafo app for Windows, what waits
   * and what failed; null until loaded or on a device that does not print.
   */
  printStatus: PrintStatus | null;
  /** Goes up when an order or a Z changed on any device (Rapporten & Z reloads the X and the archive). */
  reportsRevision: number;
  /** Goes up when the platforms or the accountant's e-mail changed on any device. */
  reportSettingsRevision: number;
  /** Goes up when the licence, an invoice or the terms changed (Abonnement & facturen reloads). */
  subscriptionRevision: number;
  /** Clocking in and out (Tijdsregistratie): on or off, and how many are clocked in; null until loaded. */
  time: TimeStatus | null;
  /** After switching clocking on or off here (the live update follows elsewhere). */
  timeChanged: (time: TimeStatus) => void;
  /** This device's own name and kind changed (renamed in Beheer). */
  deviceChanged: (device: DeviceSummary) => void;
  /** Payment methods of this till (anyone may take payments, no code needed). */
  paymentMethods: PaymentMethod[];
  /**
   * Asks for a personal code with the given permission (the code window). Resolves with who gave
   * it and a short-lived token for the action, or null when the window was closed.
   */
  authorize: (permission: CodePurpose, title: string) => Promise<Authorization | null>;
  /**
   * Shows an error to the cashier. Errors that change what the till can do (device unpaired, terms,
   * shop paused) are handled centrally and move the till to the right screen.
   */
  report: (error: unknown) => void;
  toast: (message: string, tone?: "info" | "bad") => void;
}

export const TillContext = createContext<Till | null>(null);

export function useTill(): Till {
  const till = useContext(TillContext);
  if (till === null) throw new Error("useTill() outside the till");
  return till;
}
